import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { BillingSnapshot, billingApi, planLabel } from "@/lib/billing-api";
import { usePlan } from "@/lib/use-plan";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function MockPortal() {
  const navigate = useNavigate();
  const { refresh } = usePlan();
  const [snap, setSnap] = useState<BillingSnapshot | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    try {
      const s = await billingApi.snapshot();
      setSnap(s);
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not load.");
    }
  }
  useEffect(() => { load(); }, []);

  async function cancel() {
    setBusy("cancel");
    try {
      await billingApi.cancel();
      toast.success("Cancellation scheduled.");
      await load(); await refresh();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not cancel.");
    } finally { setBusy(null); }
  }
  async function resume() {
    setBusy("resume");
    try {
      await billingApi.resume();
      toast.success("Subscription resumed.");
      await load(); await refresh();
    } catch (e: any) {
      toast.error(e instanceof ApiError ? e.message : "Could not resume.");
    } finally { setBusy(null); }
  }

  return (
    <main className="min-h-screen bg-bone/40 grid place-items-center px-6 py-16"
      data-testid="mock-portal-page">
      <div className="w-full max-w-md">
        <span className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          Mock customer portal — preview environment
        </span>
        <div className="border border-border bg-background p-8 mt-6">
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Manage subscription</p>
          {!snap ? (
            <Skeleton className="h-32" />
          ) : (
            <>
              <h1 className="font-display text-3xl">{planLabel(snap.effective_plan)}</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                {snap.subscription.cancel_at_period_end
                  ? `Cancellation scheduled — access until ${snap.subscription.current_period_end ? new Date(snap.subscription.current_period_end).toLocaleDateString() : "—"}.`
                  : snap.subscription.current_period_end
                    ? `Renews ${new Date(snap.subscription.current_period_end).toLocaleDateString()}.`
                    : "No active subscription."}
              </p>
              <p className="mt-4 text-sm text-foreground/80">
                Card on file:{" "}
                <span className="font-mono">
                  {snap.card_brand && snap.card_last4
                    ? `${snap.card_brand} •••• ${snap.card_last4}`
                    : "—"}
                </span>
              </p>

              <div className="mt-6 space-y-3">
                {snap.subscription.cancel_at_period_end ? (
                  <Button onClick={resume} disabled={busy === "resume"}
                    data-testid="portal-resume-btn"
                    className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
                    {busy === "resume" ? "Resuming…" : "Resume subscription"}
                  </Button>
                ) : (
                  <Button variant="outline" onClick={cancel} disabled={busy === "cancel"}
                    data-testid="portal-cancel-btn"
                    className="w-full rounded-none border-oxblood/40 text-oxblood hover:bg-oxblood/5 h-11">
                    {busy === "cancel" ? "Cancelling…" : "Cancel at period end"}
                  </Button>
                )}
                <Button asChild variant="outline"
                  className="w-full rounded-none border-foreground/20 hover:bg-bone h-11"
                  data-testid="portal-back-btn">
                  <Link to="/app/settings/billing">Back to billing</Link>
                </Button>
              </div>
            </>
          )}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          In production this is replaced by Stripe's hosted customer portal.
        </p>
      </div>
    </main>
  );
}
